'use client';

import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Trash2, TestTube2, Eye, EyeOff, Plus, Loader2, AlertCircle, Pencil, History } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/api/trpc';
import { WebhookDeliveriesDialog } from './webhook-deliveries-dialog';

// API Actions imports (tRPC hooks)
import {
  useWebhookEventTypes,
  useWebhookEndpoints,
  useCreateWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useTestWebhookEndpoint,
  useWebhookEndpointsDeliveryStatus,
} from '@/lib/api/integrations';

// ============================================================================
// Types & Schema
// ============================================================================

const webhookFormSchema = z.object({
  url: z.string().url('Please enter a valid URL'),
  description: z.string().optional(),
  eventTypes: z.array(z.string()).min(1, 'Please select at least one event type'),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof webhookFormSchema>;

interface WebhookEndpoint {
  id: string;
  url: string;
  description?: string | null;
  enabled: boolean;
  disabledReason?: string | null;
  firstAttemptAt?: Date | string | null;
  lastSuccessfulDeliveryAt?: Date | string | null;
  secret?: string;
  eventTypes: string[];
  createdAt: Date | null;
  updatedAt: Date | null;
}

interface WebhooksFormProps {
  defaultValues?: Record<string, any>;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  isLoading?: boolean;
  storeId: string;
}

const EVENT_CATEGORIES: Array<{ key: string; labelKey: string; prefix: string }> = [
  { key: 'order', labelKey: 'webhooks.categories.orders', prefix: 'order/' },
  { key: 'payment', labelKey: 'webhooks.categories.payments', prefix: 'payment/' },
  { key: 'checkout', labelKey: 'webhooks.categories.checkout', prefix: 'checkout/' },
  { key: 'subscription', labelKey: 'webhooks.categories.subscriptions', prefix: 'subscription/' },
  { key: 'funnel', labelKey: 'webhooks.categories.funnels', prefix: 'funnel/' },
  { key: 'club', labelKey: 'webhooks.categories.club', prefix: 'club/' },
  { key: 'security', labelKey: 'webhooks.categories.security', prefix: 'security/' },
];

const EVENT_LABEL_KEYS: Record<string, string> = {
  'order/paid': 'webhooks.eventLabels.orderPaid',
  'order/created': 'webhooks.eventLabels.orderCreated',
  'order/refunded': 'webhooks.eventLabels.orderRefunded',
  'order/failed': 'webhooks.eventLabels.orderFailed',
  'order/upsellStarted': 'webhooks.eventLabels.orderUpsellStarted',
  'order/paymentInitiated': 'webhooks.eventLabels.orderPaymentInitiated',
  'order/syncFailed': 'webhooks.eventLabels.orderSyncFailed',
  'checkout/initiated': 'webhooks.eventLabels.checkoutInitiated',
  'checkout/emailValidated': 'webhooks.eventLabels.checkoutEmailValidated',
  'payment/created': 'webhooks.eventLabels.paymentCreated',
  'payment/succeeded': 'webhooks.eventLabels.paymentSucceeded',
  'payment/failed': 'webhooks.eventLabels.paymentFailed',
  'payment/refunded': 'webhooks.eventLabels.paymentRefunded',
  'payment/authorized': 'webhooks.eventLabels.paymentAuthorized',
  'payment/rejected': 'webhooks.eventLabels.paymentRejected',
  'subscription/created': 'webhooks.eventLabels.subscriptionCreated',
  'subscription/canceled': 'webhooks.eventLabels.subscriptionCanceled',
  'subscription/paused': 'webhooks.eventLabels.subscriptionPaused',
  'subscription/resumed': 'webhooks.eventLabels.subscriptionResumed',
  'subscription/pastDue': 'webhooks.eventLabels.subscriptionPastDue',
  'subscription/rebillUpcoming': 'webhooks.eventLabels.subscriptionRebillUpcoming',
  'subscription/rebillSucceeded': 'webhooks.eventLabels.subscriptionRebillSucceeded',
  'subscription/rebillDeclined': 'webhooks.eventLabels.subscriptionRebillDeclined',
  'subscription/cancelScheduled': 'webhooks.eventLabels.subscriptionCancelScheduled',
  'subscription/rebillCaptureFailed': 'webhooks.eventLabels.subscriptionRebillCaptureFailed',
  'funnel/sessionStarted': 'webhooks.eventLabels.funnelSessionStarted',
  'funnel/sessionCompleted': 'webhooks.eventLabels.funnelSessionCompleted',
  'funnel/stepEntered': 'webhooks.eventLabels.funnelStepEntered',
  'funnel/stepViewed': 'webhooks.eventLabels.funnelStepViewed',
  'funnel/stepExited': 'webhooks.eventLabels.funnelStepExited',
  'funnel/converted': 'webhooks.eventLabels.funnelConverted',
  'funnel/customEvent': 'webhooks.eventLabels.funnelCustomEvent',
  'funnel/sessionAbandoned': 'webhooks.eventLabels.funnelSessionAbandoned',
  'club/membershipActivated': 'webhooks.eventLabels.clubMembershipActivated',
  'club/membershipDeactivated': 'webhooks.eventLabels.clubMembershipDeactivated',
  'security/abuseDetected': 'webhooks.eventLabels.securityAbuseDetected',
};

function getEventLabel(eventType: string, t: (key: string) => string): string {
  const key = EVENT_LABEL_KEYS[eventType];
  if (key) return t(key);
  return eventType.split('/').pop() || eventType;
}

const OTHER_CATEGORY_KEY = 'webhooks.categories.other';

function groupEventTypesByCategory(eventTypes: string[]) {
  const groups: Record<string, string[]> = {};
  for (const cat of EVENT_CATEGORIES) {
    const matching = eventTypes.filter((t) => t.startsWith(cat.prefix));
    if (matching.length > 0) groups[cat.labelKey] = matching;
  }
  const categorized = new Set(Object.values(groups).flat());
  const uncategorized = eventTypes.filter((t) => !categorized.has(t));
  if (uncategorized.length > 0) groups[OTHER_CATEGORY_KEY] = uncategorized;
  return groups;
}

// ============================================================================
// Component
// ============================================================================

export function WebhooksForm({
  defaultValues = {},
  onSubmit,
  isLoading: externalLoading = false,
  storeId,
}: WebhooksFormProps) {
  const { t } = useTranslation('integrations');
  const { toast } = useToast();

  // tRPC hooks
  const utils = trpc.useUtils();
  const {
    data: webhooksData,
    isLoading: isLoadingWebhooks,
    refetch: refetchWebhooks,
  } = useWebhookEndpoints(storeId, !!storeId);
  const {
    data: eventTypesData,
    isLoading: isLoadingEventTypes,
    error: eventTypesError,
  } = useWebhookEventTypes();
  const { data: deliveryStatusData, isSuccess: isDeliveryStatusLoaded } = useWebhookEndpointsDeliveryStatus(
    storeId,
    !!storeId,
  );

  const createWebhook = useCreateWebhookEndpoint();
  const updateWebhook = useUpdateWebhookEndpoint();
  const deleteWebhook = useDeleteWebhookEndpoint();
  const testWebhook = useTestWebhookEndpoint();

  // State - safely extract data
  const webhooks = React.useMemo(() => {
    if (!webhooksData) return [];
    if (!Array.isArray(webhooksData)) return [];
    return webhooksData.filter(
      (w: any) => w && typeof w === 'object' && 'id' in w,
    ) as unknown as WebhookEndpoint[];
  }, [webhooksData]);

  const availableEventTypes = React.useMemo(() => {
    if (!eventTypesData) return [];
    if (Array.isArray(eventTypesData)) return eventTypesData;
    if (typeof eventTypesData === 'object' && 'eventTypes' in eventTypesData) {
      const eventTypes = (eventTypesData as any).eventTypes;
      if (Array.isArray(eventTypes)) return eventTypes;
    }
    return [];
  }, [eventTypesData]);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookEndpoint | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [webhookToDelete, setWebhookToDelete] = useState<WebhookEndpoint | null>(null);
  const [secretVisible, setSecretVisible] = useState<Record<string, boolean>>({});
  const [webhookSecrets, setWebhookSecrets] = useState<Record<string, string>>({});
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [testingEventType, setTestingEventType] = useState<string | null>(null);
  const [deliveriesWebhook, setDeliveriesWebhook] = useState<WebhookEndpoint | null>(null);
  const [isDeliveriesDialogOpen, setIsDeliveriesDialogOpen] = useState(false);

  const deliveryStatusByEndpointId = React.useMemo(() => {
    const map: Record<string, { status: string; responseStatus: number | null; lastDeliveryAt: string }> = {};
    for (const entry of deliveryStatusData || []) {
      map[entry.webhookEndpointId] = entry;
    }
    return map;
  }, [deliveryStatusData]);

  // Form
  const form = useForm<FormValues>({
    resolver: zodResolver(webhookFormSchema),
    defaultValues: {
      url: '',
      description: '',
      eventTypes: [],
      enabled: true,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = form;

  // Fetch webhook secret when needed
  const fetchWebhookSecret = async (webhookId: string) => {
    try {
      const result: any = await utils.integrations.getWebhookEndpoint.fetch({ id: webhookId });
      if (result && result.secret) {
        setWebhookSecrets((prev) => ({
          ...prev,
          [webhookId]: String(result.secret),
        }));
      }
    } catch (error) {
      console.error('Failed to fetch webhook secret:', error);
      toast({
        title: t('webhooks.fetchSecretError'),
        description: t('webhooks.fetchSecretErrorDescription'),
        variant: 'destructive',
      });
    }
  };

  // Handle create/update webhook
  const handleFormSubmit = async (data: FormValues) => {
    if (selectedWebhook) {
      // Update existing webhook
      try {
        await updateWebhook.mutateAsync({
          id: selectedWebhook.id,
          ...data,
        });
        setIsDialogOpen(false);
        setSelectedWebhook(null);
        reset();
        await refetchWebhooks();
      } catch (error) {
        // Error handling is done in the mutation hook
      }
    } else {
      // Create new webhook
      try {
        await createWebhook.mutateAsync({
          storeId,
          ...data,
        });
        setIsDialogOpen(false);
        reset();
        await refetchWebhooks();
      } catch (error) {
        // Error handling is done in the mutation hook
      }
    }
  };

  // Handle delete webhook
  const handleDeleteWebhook = async () => {
    if (!webhookToDelete) return;

    try {
      await deleteWebhook.mutateAsync({ id: webhookToDelete.id });
      setIsDeleteDialogOpen(false);
      setWebhookToDelete(null);
      await refetchWebhooks();
    } catch (error) {
      // Error handling is done in the mutation hook
    }
  };

  // Handle test webhook — optionally with a specific event type
  const handleTestWebhook = async (webhookId: string, eventType?: string) => {
    setTestingWebhook(webhookId);
    setTestingEventType(eventType || null);
    try {
      await testWebhook.mutateAsync({ id: webhookId, ...(eventType ? { eventType } : {}) });
    } catch (error) {
      // Error handling is done in the mutation hook
    } finally {
      setTestingWebhook(null);
      setTestingEventType(null);
    }
  };

  const handleReEnableWebhook = async (webhookId: string) => {
    try {
      await updateWebhook.mutateAsync({ id: webhookId, enabled: true });
      await refetchWebhooks();
    } catch {
      void 0;
    }
  };

  // Handle edit webhook
  const handleEditWebhook = (webhook: WebhookEndpoint) => {
    setSelectedWebhook(webhook);
    reset({
      url: webhook.url,
      description: webhook.description || '',
      eventTypes: webhook.eventTypes,
      enabled: webhook.enabled,
    });
    setIsDialogOpen(true);
  };

  // Handle add webhook
  const handleAddWebhook = () => {
    setSelectedWebhook(null);
    reset({
      url: '',
      description: '',
      eventTypes: [],
      enabled: true,
    });
    setIsDialogOpen(true);
  };

  // Toggle secret visibility
  const toggleSecretVisibility = async (webhookId: string) => {
    if (!secretVisible[webhookId] && !webhookSecrets[webhookId]) {
      await fetchWebhookSecret(webhookId);
    }

    setSecretVisible((prev) => ({
      ...prev,
      [webhookId]: !prev[webhookId],
    }));
  };

  // Event type selection handlers
  const handleEventTypeToggle = (eventType: string) => {
    const currentEventTypes = watch('eventTypes') || [];
    const newEventTypes = currentEventTypes.includes(eventType)
      ? currentEventTypes.filter((type) => type !== eventType)
      : [...currentEventTypes, eventType];
    setValue('eventTypes', newEventTypes);
  };

  const handleSelectAll = () => {
    setValue('eventTypes', availableEventTypes);
  };

  const handleDeselectAll = () => {
    setValue('eventTypes', []);
  };

  // Group event types by category
  const eventsByCategory = groupEventTypesByCategory(availableEventTypes);

  return (
    <div className="space-y-6">
      {/* Enable/Disable Integration Toggle */}
      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('webhooks.aboutTitle')}</AlertTitle>
        <AlertDescription>{t('webhooks.aboutDescription')}</AlertDescription>
      </Alert>

      {/* Webhooks List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('webhooks.endpointsTitle')}</CardTitle>
              <CardDescription>{t('webhooks.endpointsDescription')}</CardDescription>
            </div>
            <Button onClick={handleAddWebhook} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              {t('webhooks.addWebhook')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingWebhooks ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
            </div>
          ) : !webhooks || webhooks.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-4">{t('webhooks.noWebhooks')}</p>
              <Button onClick={handleAddWebhook} variant="outline">
                {t('webhooks.createFirst')}
              </Button>
            </div>
          ) : (
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50%]">{t('webhooks.endpoint')}</TableHead>
                    <TableHead>{t('webhooks.events')}</TableHead>
                    <TableHead>{t('webhooks.status')}</TableHead>
                    <TableHead className="text-right">{t('webhooks.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook: WebhookEndpoint) => (
                    <TableRow key={webhook.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="max-w-[300px] truncate font-mono text-sm" title={webhook.url}>
                            {webhook.url}
                          </span>
                          {webhook.description && (
                            <span className="text-muted-foreground max-w-[300px] truncate text-xs">
                              {webhook.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="outline" className="cursor-help">
                              {t('webhooks.eventsCount', { count: webhook.eventTypes?.length ?? 0 })}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-[300px]">
                            <div className="flex flex-wrap gap-1">
                              {webhook.eventTypes?.map((eventType: string) => (
                                <Badge key={eventType} variant="secondary" className="text-xs">
                                  {eventType}
                                </Badge>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {webhook.disabledReason === 'auto_no_success_3d' ? (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="destructive" className="cursor-help text-xs">
                                    {t('webhooks.autoDisabled')}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[280px]">
                                  {t('webhooks.autoDisabledTooltip')}
                                </TooltipContent>
                              </Tooltip>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleReEnableWebhook(webhook.id)}
                                disabled={updateWebhook.isPending}
                              >
                                {updateWebhook.isPending ? (
                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                ) : null}
                                {t('webhooks.reEnable')}
                              </Button>
                            </>
                          ) : (
                            <Badge variant={webhook.enabled ? 'green' : 'secondary'} className="text-xs">
                              {webhook.enabled ? t('webhooks.active') : t('webhooks.off')}
                            </Badge>
                          )}
                          {(() => {
                            const delivery = deliveryStatusByEndpointId[webhook.id];
                            if (!delivery) {
                              // Claiming "never delivered" while the query is loading or failed
                              // would accuse a healthy endpoint of being silent.
                              if (!isDeliveryStatusLoaded) {
                                return null;
                              }
                              return <span className="text-muted-foreground text-xs">{t('webhooks.neverDelivered')}</span>;
                            }
                            const succeeded = delivery.status === 'succeeded';
                            return (
                              <span
                                className={`text-xs ${succeeded ? 'text-muted-foreground' : 'text-destructive font-medium'}`}
                              >
                                {succeeded ? t('webhooks.delivered') : t('webhooks.deliveryFailed')}
                                {delivery.responseStatus !== null && ` (${delivery.responseStatus})`}
                              </span>
                            );
                          })()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => toggleSecretVisibility(webhook.id)}
                              >
                                {secretVisible[webhook.id] ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {secretVisible[webhook.id] ? t('webhooks.hideSecret') : t('webhooks.showSecret')}
                            </TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => {
                                  setDeliveriesWebhook(webhook);
                                  setIsDeliveriesDialogOpen(true);
                                }}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('webhooks.deliveryHistory')}</TooltipContent>
                          </Tooltip>
                          <DropdownMenu>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8"
                                    disabled={testingWebhook === webhook.id}
                                  >
                                    {testingWebhook === webhook.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <TestTube2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                              </TooltipTrigger>
                              <TooltipContent>{t('webhooks.testWebhook')}</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="end" className="max-h-80 w-72 overflow-y-auto">
                              <DropdownMenuLabel>{t('webhooks.sendTestEvent')}</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleTestWebhook(webhook.id)}
                                disabled={testingWebhook === webhook.id}
                              >
                                <TestTube2 className="text-muted-foreground mr-2 h-3.5 w-3.5" />
                                {t('webhooks.genericTest')}
                              </DropdownMenuItem>
                              {webhook.eventTypes && webhook.eventTypes.length > 0 && (
                                <>
                                  <DropdownMenuSeparator />
                                  {Object.entries(groupEventTypesByCategory(webhook.eventTypes)).map(
                                    ([category, events]) => (
                                      <React.Fragment key={category}>
                                        <DropdownMenuLabel className="text-muted-foreground pt-1 text-xs font-normal">
                                          {t(category)}
                                        </DropdownMenuLabel>
                                        {events.map((eventType: string) => (
                                          <DropdownMenuItem
                                            key={eventType}
                                            onClick={() => handleTestWebhook(webhook.id, eventType)}
                                            disabled={testingWebhook === webhook.id}
                                            className="pl-4"
                                          >
                                            {testingWebhook === webhook.id &&
                                            testingEventType === eventType ? (
                                              <Loader2 className="mr-2 h-3.5 w-3.5 shrink-0 animate-spin" />
                                            ) : (
                                              <span className="mr-2 inline-block h-3.5 w-3.5 shrink-0" />
                                            )}
                                            <span className="text-sm">{getEventLabel(eventType, t)}</span>
                                            <span className="text-muted-foreground ml-auto font-mono text-xs">
                                              {eventType}
                                            </span>
                                          </DropdownMenuItem>
                                        ))}
                                      </React.Fragment>
                                    ),
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => handleEditWebhook(webhook)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('webhooks.edit')}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="text-destructive hover:text-destructive h-8 w-8"
                                onClick={() => {
                                  setWebhookToDelete(webhook);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('webhooks.delete')}</TooltipContent>
                          </Tooltip>
                        </div>
                        {/* Show secret inline when revealed */}
                        {secretVisible[webhook.id] && (
                          <div
                            className="text-muted-foreground mt-1 max-w-[200px] truncate font-mono text-xs"
                            title={webhookSecrets[webhook.id]}
                          >
                            {webhookSecrets[webhook.id] || t('webhooks.loading')}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {selectedWebhook ? t('webhooks.editWebhook') : t('webhooks.createNewWebhook')}
            </DialogTitle>
            <DialogDescription>
              {selectedWebhook ? t('webhooks.editDescription') : t('webhooks.createDescription')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
            {/* URL */}
            <div className="space-y-2">
              <Label htmlFor="url">
                {t('webhooks.webhookUrl')}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="url"
                type="url"
                placeholder="https://example.com/webhooks"
                disabled={createWebhook.isPending || updateWebhook.isPending}
                {...register('url')}
              />
              {errors.url && <p className="text-destructive text-xs">{errors.url.message}</p>}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('webhooks.descriptionOptional')}</Label>
              <Input
                id="description"
                placeholder={t('webhooks.descriptionPlaceholder')}
                disabled={createWebhook.isPending || updateWebhook.isPending}
                {...register('description')}
              />
            </div>

            {/* Enabled Toggle */}
            <div className="flex items-center space-x-3">
              <Checkbox
                id="webhook-enabled"
                checked={watch('enabled')}
                onCheckedChange={(checked) => setValue('enabled', Boolean(checked))}
                disabled={createWebhook.isPending || updateWebhook.isPending}
              />
              <Label htmlFor="webhook-enabled">{t('webhooks.enableWebhook')}</Label>
            </div>

            {/* Event Types Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t('webhooks.eventTypes')}</Label>
                  <p className="text-muted-foreground text-sm">{t('webhooks.eventTypesDescription')}</p>
                  {isLoadingEventTypes && <p className="text-primary mt-1 text-sm">{t('webhooks.loadingEventTypes')}</p>}
                  {eventTypesError && (
                    <p className="text-destructive mt-1 text-sm">{t('webhooks.failedToLoadEventTypes')}</p>
                  )}
                  {!isLoadingEventTypes && availableEventTypes.length === 0 && (
                    <p className="mt-1 text-sm text-amber-600">{t('webhooks.noEventTypesAvailable')}</p>
                  )}
                  {errors.eventTypes && (
                    <p className="text-destructive mt-1 text-sm">{errors.eventTypes.message}</p>
                  )}
                </div>
                {availableEventTypes.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleSelectAll}
                      disabled={createWebhook.isPending || updateWebhook.isPending}
                    >
                      {t('webhooks.selectAll')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleDeselectAll}
                      disabled={createWebhook.isPending || updateWebhook.isPending}
                    >
                      {t('webhooks.deselectAll')}
                    </Button>
                  </div>
                )}
              </div>

              {Object.entries(eventsByCategory).map(([category, events]) => {
                const selectedInCategory = (watch('eventTypes') || []).filter((t) => events.includes(t));
                const allSelected = selectedInCategory.length === events.length;

                const toggleCategory = () => {
                  const currentEventTypes = watch('eventTypes') || [];
                  if (allSelected) {
                    setValue(
                      'eventTypes',
                      currentEventTypes.filter((t) => !events.includes(t)),
                    );
                  } else {
                    const merged = new Set([...currentEventTypes, ...events]);
                    setValue('eventTypes', [...merged]);
                  }
                };

                return (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">{t(category)}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={toggleCategory}
                        disabled={createWebhook.isPending || updateWebhook.isPending}
                      >
                        {allSelected ? t('webhooks.deselectAll') : t('webhooks.selectAll')}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {events.map((eventType) => (
                        <div key={eventType} className="flex items-center space-x-3">
                          <Checkbox
                            id={`event-${eventType}`}
                            checked={(watch('eventTypes') || []).includes(eventType)}
                            onCheckedChange={() => handleEventTypeToggle(eventType)}
                            disabled={createWebhook.isPending || updateWebhook.isPending}
                          />
                          <Label
                            htmlFor={`event-${eventType}`}
                            className="flex cursor-pointer items-center gap-2 text-sm font-normal"
                          >
                            {getEventLabel(eventType, t)}
                            <span className="text-muted-foreground font-mono text-xs">{eventType}</span>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={createWebhook.isPending || updateWebhook.isPending}
              >
                {t('webhooks.cancel')}
              </Button>
              <Button type="submit" disabled={createWebhook.isPending || updateWebhook.isPending}>
                {createWebhook.isPending || updateWebhook.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {selectedWebhook ? t('webhooks.updating') : t('webhooks.creating')}
                  </>
                ) : selectedWebhook ? (
                  t('webhooks.updateWebhook')
                ) : (
                  t('webhooks.createWebhook')
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('webhooks.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('webhooks.deleteConfirmDescription')}{' '}
              <code className="font-mono text-sm">{webhookToDelete?.url}</code>{t('webhooks.deleteConfirmSuffix')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWebhook.isPending}>{t('webhooks.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWebhook}
              disabled={deleteWebhook.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteWebhook.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('webhooks.deleting')}
                </>
              ) : (
                t('webhooks.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <WebhookDeliveriesDialog
        endpoint={deliveriesWebhook}
        open={isDeliveriesDialogOpen}
        onOpenChange={setIsDeliveriesDialogOpen}
      />
    </div>
  );
}
